import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useAbp } from 'core/hooks/useAbp';
import { useRole } from 'features/auth/contexts/RoleContext';
import { RoleNames, isSameRoleName } from 'core/roles/roleNames';
import { GenericLoading } from 'components/ui/Loader';

interface PermissionGuardProps {
  policy?: string;
  policies?: readonly string[] | string[];
  requireAll?: boolean;
  fallback?: string;
  fallbackComponent?: ReactNode;
  requiresAuth?: boolean;
  children: ReactNode;
}

export const PermissionGuard = ({
  policy,
  policies = [],
  requireAll = false,
  fallback = '/bord',
  fallbackComponent,
  requiresAuth = true,
  children
}: PermissionGuardProps) => {
  const auth = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, initialized } = useAbp();
  const { currentRole, isLoading: roleLoading } = useRole();
  const location = useLocation();

  // Zone back-office vs espace pro : arbitrée par le RÔLE ACTIF (casquette), pas par
  // la liste complète des rôles — un admin+Courtier passé en casquette Courtier
  // navigue dans l'espace pro sans redirection forcée.
  const isAdminActif = isSameRoleName(currentRole?.name, RoleNames.Admin);

  // Attendre que ABP soit initialisé
  if (!initialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <GenericLoading loading isParentSized />
          <p className="text-sm text-gray-600">Chargement des permissions...</p>
        </div>
      </div>
    );
  }

  // Vérification de l'authentification avec react-oidc-context
  if (requiresAuth && !auth.isAuthenticated) {
    // Sauvegarder la route courante pour redirection après login
    sessionStorage.setItem('redirectUrl', location.pathname + location.search);
    // Déclencher la connexion OIDC
    auth.signinRedirect();
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <GenericLoading loading isParentSized />
          <p className="text-sm text-gray-600">Redirection vers la connexion...</p>
        </div>
      </div>
    );
  }

  // Construction de la liste des permissions
  const allPolicies = policy ? [policy, ...policies] : policies;

  if (!roleLoading && currentRole) {
    if (location.pathname.includes('back-office') && !isAdminActif) {
      return <Navigate to="/bord" replace />;
    }

    if (isAdminActif && !location.pathname.includes('back-office')) {
      return <Navigate to="/back-office" replace />;
    }
  }

  // Si pas de permissions spécifiées, on autorise l'accès
  if (allPolicies.length === 0) {
    return <>{children}</>;
  }

  // Vérification des permissions ABP
  let hasAccess = false;

  if (allPolicies.length === 1) {
    hasAccess = hasPermission(allPolicies[0]);
  } else if (requireAll) {
    hasAccess = hasAllPermissions(allPolicies);
  } else {
    hasAccess = hasAnyPermission(allPolicies);
  }

  if (!hasAccess) {
    return fallbackComponent ? <>{fallbackComponent}</> : <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
};

